import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useMyResellerOrders, useResellerStats } from '@/hooks/useResellerOrders';
import { OrderStatusBadge, formatMoney } from '@/components/reseller/OrderStatusBadge';
import { PlusCircle, DollarSign, Clock, CheckCircle2, Users } from 'lucide-react';

export default function ResellerDashboard() {
  const { data: orders, isLoading } = useMyResellerOrders();
  const stats = useResellerStats(orders);
  const recent = (orders ?? []).slice(0, 10);

  const kpis = [
    { label: 'Pending', value: stats.pending, icon: Clock },
    { label: 'Active Licenses', value: stats.activated, icon: CheckCircle2 },
    { label: 'Customers', value: stats.uniqueCustomers, icon: Users },
    { label: 'Total Revenue', value: formatMoney(stats.totalRevenue), icon: DollarSign },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Overview</h2>
          <p className="text-muted-foreground">Track your customer orders and licenses.</p>
        </div>
        <Button asChild>
          <Link to="/reseller/orders/new"><PlusCircle className="mr-2 h-4 w-4" />Enroll Customer</Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <Card key={k.label}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{k.label}</CardTitle>
              <k.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent><div className="text-2xl font-bold">{k.value}</div></CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle>Recent Orders</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : recent.length === 0 ? (
            <p className="text-muted-foreground">No orders yet. Enroll your first customer to get started.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recent.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell className="font-mono text-xs">{o.order_number}</TableCell>
                    <TableCell>
                      <div className="font-medium">{o.customer_name ?? '—'}</div>
                      <div className="text-xs text-muted-foreground">{o.customer_email}</div>
                    </TableCell>
                    <TableCell>{formatMoney(Number(o.amount), o.currency)}</TableCell>
                    <TableCell><OrderStatusBadge status={o.status} /></TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(o.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>How this works</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p><strong>1.</strong> Click <em>Enroll Customer</em> to record a new sale. It starts as <em>Pending</em>.</p>
          <p><strong>2.</strong> An admin reviews and approves the order.</p>
          <p><strong>3.</strong> Once activated, a license key is issued and emailed to your customer automatically.</p>
          <p><strong>4.</strong> Track pipeline and revenue on this dashboard; find license keys under <em>Licenses</em>.</p>
        </CardContent>
      </Card>
    </div>
  );
}
